import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FileText,
  MessageSquare,
  Home,
  BarChart2,
  Phone,
  BookText,
  ArrowLeft,
} from 'lucide-react';
import SectionHeader from '../ui/SectionHeader';
import NewsSynthesizer from '../NewsSynthesizer';
import SocialMedia from '../SocialMedia';
import PropertyDescriptionV2 from '../PropertyDescriptionv2';
import SentimentAnalysis from '../SentimentAnalysis';
import RealTimeConversation from '../RealTimeConversation';
import PildorasSonneil from '../PildorasSonneil';
import ReviewsAnalysis from '../ReviewsAnalysis';

const aiTools = [
  {
    id: 'news-synthesizer',
    title: 'News Synthesizer',
    description: 'Analiza y sintetiza artículos de noticias automáticamente',
    icon: FileText,
    color: 'bg-info/10 text-info',
  },
  {
    id: 'social-media',
    title: 'Social Media Posts',
    description: 'Genera publicaciones para redes sociales desde noticias',
    icon: MessageSquare,
    color: 'bg-success/10 text-success',
  },
  {
    id: 'property-description-v2',
    title: 'Property Description',
    description: 'Genera descripciones SEO-friendly para propiedades inmobiliarias',
    icon: Home,
    color: 'bg-primary-lighter text-primary',
  },
  {
    id: 'sentiment-analysis',
    title: 'Sentiment Analysis',
    description: 'Analiza el sentimiento de reseñas y menciones',
    icon: BarChart2,
    color: 'bg-warning/10 text-warning',
  },
  {
    id: 'real-time-conversation',
    title: 'Real-Time Conversation',
    description: 'Agente de voz para comunicación con clientes',
    icon: Phone,
    color: 'bg-error/10 text-error',
  },
  {
    id: 'pildoras-sonneil',
    title: 'Píldoras Sonneil',
    description: 'Resume noticias inmobiliarias de forma concisa',
    icon: BookText,
    color: 'bg-purple-100 text-purple-600',
  },
  {
    id: 'reviews-analysis',
    title: 'Reviews Analysis',
    description: 'Analiza Google Reviews con inteligencia artificial',
    icon: BarChart2,
    color: 'bg-cyan-100 text-cyan-600',
  },
];

const AIToolsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTool, setActiveTool] = useState(null);

  useEffect(() => {
    const id = decodeURIComponent(location.hash.replace(/^#/, ''));
    if (aiTools.some((tool) => tool.id === id)) {
      setActiveTool(id);
    }
  }, [location.hash]);

  const closeTool = () => {
    setActiveTool(null);
    if (location.hash) {
      navigate('/herramientas-ia', { replace: true });
    }
  };

  const renderToolContent = () => {
    if (!activeTool) return null;

    const toolComponents = {
      'news-synthesizer': NewsSynthesizer,
      'social-media': SocialMedia,
      'property-description-v2': PropertyDescriptionV2,
      'sentiment-analysis': SentimentAnalysis,
      'real-time-conversation': RealTimeConversation,
      'pildoras-sonneil': PildorasSonneil,
      'reviews-analysis': ReviewsAnalysis,
    };

    const ToolComponent = toolComponents[activeTool];
    if (!ToolComponent) return null;

    return <ToolComponent setCurrentPage={closeTool} />;
  };

  if (activeTool) {
    return (
      <div className="animate-fade-in">
        <button
          onClick={closeTool}
          className="flex items-center gap-2 text-text-secondary hover:text-primary mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Volver a Herramientas IA</span>
        </button>
        {renderToolContent()}
      </div>
    );
  }

  return (
    <div className="animate-stagger">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary mb-2">Herramientas de Inteligencia Artificial</h1>
        <p className="text-text-secondary">
          Suite de herramientas desarrolladas por Sonneil para optimizar procesos con IA
        </p>
      </div>

      {/* Tools Grid */}
      <section className="section">
        <SectionHeader
          title="Herramientas Disponibles"
          description="Selecciona una herramienta para comenzar"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {aiTools.map((tool) => {
            const Icon = tool.icon;
            return (
              <button
                key={tool.id}
                id={tool.id}
                onClick={() => setActiveTool(tool.id)}
                className="card-interactive bg-white border border-border hover:border-primary/30 p-6 text-left scroll-mt-24"
              >
                <div className={`w-14 h-14 rounded-xl ${tool.color} flex items-center justify-center mb-4`}>
                  <Icon size={28} />
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">{tool.title}</h3>
                <p className="text-text-secondary text-sm">{tool.description}</p>
                <div className="mt-4 pt-4 border-t border-border-light">
                  <span className="text-primary text-sm font-medium">Abrir herramienta →</span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Info Section */}
      <section className="mt-8 p-6 bg-primary-lighter/20 rounded-2xl border border-primary-lighter">
        <h3 className="font-semibold text-text-primary mb-2">💡 ¿Necesitas una nueva herramienta?</h3>
        <p className="text-text-secondary text-sm">
          Si tienes una idea para una nueva herramienta de IA o quieres mejorar una existente, 
          contacta con el equipo tecnológico en{' '}
          <a href="mailto:techsupport@sonneil.com" className="text-primary hover:underline">
            techsupport@sonneil.com
          </a>
        </p>
      </section>
    </div>
  );
};

export default AIToolsPage;


